/**
 * Cihazda tutulan son izleme konumu (V6-10).
 *
 * Sunucuya 10 saniyede bir rapor gidiyor; uygulama çöker veya TV kapanırsa son
 * rapor ile kapanış arasındaki konum kaybolabiliyordu. Bu taslak, aynı kullanıcı
 * ve içerik için cihazda en güncel konumu tutar; bir sonraki açılışta sunucu
 * konumundan daha yeniyse başlangıç noktası olarak kullanılır.
 *
 * Taslak hesap ve içerikle anahtarlanır; cihazda kalan oynatıcı tercihlerinden
 * ayrıdır. Kalıcı veri değildir, 7 gün sonra kendiliğinden yok sayılır.
 */
import type { ResumeInput } from './resume.ts';
import { NEAR_END_MS, resumePositionMs } from './resume.ts';
import { recordSwallowed } from './swallowed.ts';

const PREFIX = 'pbfork.progressDraft.v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface ProgressDraft {
  positionMs: number;
  durationMs: number | null;
  updatedAt: number;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch (error) {
    recordSwallowed('progressDraft', error);
    // Gizli sekme / depolama kapalı.
    return null;
  }
}

function keyOf(userId: string, itemId: string): string {
  return `${PREFIX}.${userId}.${itemId}`;
}

export function readProgressDraft(
  userId: string,
  itemId: string,
  now = Date.now(),
): ProgressDraft | null {
  const store = storage();
  if (store === null) return null;
  try {
    const raw = store.getItem(keyOf(userId, itemId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const value = parsed as Record<string, unknown>;
    if (
      typeof value.positionMs !== 'number' ||
      !Number.isFinite(value.positionMs) ||
      value.positionMs < 0 ||
      typeof value.updatedAt !== 'number' ||
      !Number.isFinite(value.updatedAt)
    ) {
      return null;
    }
    if (now - value.updatedAt > TTL_MS) {
      store.removeItem(keyOf(userId, itemId));
      return null;
    }
    return {
      positionMs: value.positionMs,
      durationMs:
        typeof value.durationMs === 'number' && Number.isFinite(value.durationMs)
          ? value.durationMs
          : null,
      updatedAt: value.updatedAt,
    };
  } catch (error) {
    recordSwallowed('progressDraft', error);
    return null;
  }
}

/** Son temizlik zamanı: her yazmada tüm anahtarları taramayalım. */
let lastPrune = 0;
const PRUNE_INTERVAL_MS = 5 * 60 * 1000;

/** TTL'i aşmış taslakları (diğer kullanıcılar dâhil) temizler. */
function pruneExpired(store: Storage, now: number): void {
  if (now - lastPrune < PRUNE_INTERVAL_MS) return;
  lastPrune = now;
  try {
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const key = store.key(index);
      if (key === null || !key.startsWith(PREFIX)) continue;
      try {
        const parsed: unknown = JSON.parse(store.getItem(key) ?? 'null');
        const updatedAt =
          typeof parsed === 'object' && parsed !== null
            ? (parsed as { updatedAt?: unknown }).updatedAt
            : undefined;
        if (typeof updatedAt !== 'number' || now - updatedAt > TTL_MS) store.removeItem(key);
      } catch (error) {
        recordSwallowed('progressDraft', error);
        store.removeItem(key);
      }
    }
  } catch (error) {
    recordSwallowed('progressDraft', error);
    // Depolama kısıtlı: temizlik bir sonraki yazmada yeniden denenir.
  }
}

export function writeProgressDraft(
  userId: string,
  itemId: string,
  positionMs: number,
  durationMs: number | null,
  now = Date.now(),
): void {
  if (!Number.isFinite(positionMs) || positionMs < 0) return;
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(
      keyOf(userId, itemId),
      JSON.stringify({
        positionMs: Math.round(positionMs),
        durationMs:
          durationMs !== null && Number.isFinite(durationMs) && durationMs > 0
            ? Math.round(durationMs)
            : null,
        updatedAt: now,
      }),
    );
    pruneExpired(store, now);
  } catch (error) {
    recordSwallowed('progressDraft', error);
    // Kota/gizli mod: taslak yalnız bu oturumda yok sayılır.
  }
}

export function clearProgressDraft(userId: string, itemId: string): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(keyOf(userId, itemId));
  } catch (error) {
    recordSwallowed('progressDraft', error);
    // Depolama kapatıldı; temizlenecek bir şey yok.
  }
}

/**
 * Sunucu konumu ile yerel taslağı birleştirir.
 *
 * İzlenmiş içerik ve sürenin son 5 saniyesi her zaman baştan başlar; bayat
 * taslak sunucu konumunun önüne geçmez.
 */
export function resolveResumeWithDraft(
  item: ResumeInput,
  draft: ProgressDraft | null,
  now = Date.now(),
): number {
  const serverMs = resumePositionMs(item);
  if (draft === null || item.isWatched) return serverMs;
  if (now - draft.updatedAt > TTL_MS) return serverMs;
  // Sunucu raporu taslaktan daha yeniyse sunucu kazanır; konum büyüklüğü
  // tek başına yeterli değildir (geri sarma emri kaybolmasın).
  if (item.progress?.updatedAt !== undefined && item.progress.updatedAt >= draft.updatedAt) {
    return serverMs;
  }
  if (draft.positionMs <= serverMs) return serverMs;
  if (item.durationMs !== null && draft.positionMs >= item.durationMs - NEAR_END_MS) return 0;
  return draft.positionMs;
}
